import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { collection, getDocs, doc, updateDoc, getDoc, setDoc, deleteDoc, query, orderBy, limit, startAfter, getCountFromServer, Timestamp } from 'firebase/firestore';
import { ref, deleteObject, getDownloadURL } from 'firebase/storage';
import { FaUser, FaEnvelope, FaPhone, FaTimes, FaTrash, FaSpinner, FaEye, FaCheck, FaLink } from 'react-icons/fa';
import { logCreativeApproval, logCreativeRejection } from '../utils/activityLogger';
import { getAllFirebaseCreativeIdsForCampaign } from '../utils/campaignCreativeIds';
import CampaignDetail from './CampaignDetail';
import dayjs from 'dayjs';

const PERION_API_BRIDGE_BASE = 'https://perion-api-bridge.fly.dev';

/** Document IDs excluded from moderation list UI */
const HIDDEN_CREATIVE_IDS = new Set(['pIgKZLYdmkNdF7UHCFu0']);

const CreativesList = () => {
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [sortAscending, setSortAscending] = useState(false); 
  const [filterStatus, setFilterStatus] = useState('All');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailCreative, setDetailCreative] = useState(null);
  const [userInfo, setUserInfo] = useState({});
  const [signInfo, setSignInfo] = useState({});
  const [allSigns, setAllSigns] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCreatives, setTotalCreatives] = useState(0);
  const [lastDocs, setLastDocs] = useState([]);
  const ITEMS_PER_PAGE = 12;
  
  // Perion submission state
  const [iabCategories, setIabCategories] = useState([]);
  const [iabCategoriesLoading, setIabCategoriesLoading] = useState(false);
  const [iabCategoriesError, setIabCategoriesError] = useState('');
  const [selectedIabByCreativeId, setSelectedIabByCreativeId] = useState({}); // { [creativeId]: iabCode }
  const [perionSubmitting, setPerionSubmitting] = useState(false);
  const [failedPreviewCreativeIds, setFailedPreviewCreativeIds] = useState(() => new Set());
  const [detailPreviewFailed, setDetailPreviewFailed] = useState(false);
  const [showSignPreviewModal, setShowSignPreviewModal] = useState(false);
  const [signPreviewCreative, setSignPreviewCreative] = useState(null);
  const [signPreviewSelectedIndex, setSignPreviewSelectedIndex] = useState(0);
  const [submissionUrlDraft, setSubmissionUrlDraft] = useState('');
  const [savingSubmissionUrl, setSavingSubmissionUrl] = useState(false);

  /** creative doc id -> [{ id: campaignId, name }] */
  const [creativeToCampaigns, setCreativeToCampaigns] = useState({});
  const [campaignIndexLoading, setCampaignIndexLoading] = useState(true);
  const [campaignIndexError, setCampaignIndexError] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [openingCampaignId, setOpeningCampaignId] = useState(null);

  const rejectionTemplates = [
    'No clear brand or identity',
    'Sign owner rejection',
    'Nudity',
    'Inappropriate content',
    'Poor image quality',
    'Does not meet size requirements'
  ];

  useEffect(() => {
    fetchTotalCount();
    fetchAllSigns();
    loadIabCategories();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCampaignCreativeIndex = async () => {
      setCampaignIndexLoading(true);
      setCampaignIndexError('');
      try {
        const querySnapshot = await getDocs(collection(db, 'campaigns'));
        const list = querySnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => !c.isWarmup && !c.iswarmup && !String(c.id || '').startsWith('warmup-'));
        const map = {};
        for (const camp of list) {
          const ids = getAllFirebaseCreativeIdsForCampaign(camp);
          const name = String(camp.name || camp.id || '').trim() || camp.id;
          const ref = { id: camp.id, name };
          for (const cid of ids) {
            if (!cid || cancelled) continue;
            if (!map[cid]) map[cid] = [];
            if (!map[cid].some((x) => x.id === ref.id)) map[cid].push(ref);
          }
        }
        if (!cancelled) setCreativeToCampaigns(map);
      } catch (e) {
        console.error('Failed to build creative–campaign index:', e);
        if (!cancelled) setCampaignIndexError(e?.message || 'Failed to load campaigns');
      } finally {
        if (!cancelled) setCampaignIndexLoading(false);
      }
    };
    loadCampaignCreativeIndex();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (filterStatus === 'All') {
      fetchCreatives();
    } else {
      fetchFilteredCreatives();
    }
  }, [page, filterStatus]);

  useEffect(() => {
    if (creatives.length > 0) {
      fetchMissingSignInfo();
    }
  }, [creatives, allSigns]);

  const fetchTotalCount = async () => {
    try {
      const countSnap = await getCountFromServer(collection(db, 'creatives'));
      setTotalCreatives(countSnap.data().count);
    } catch (error) {
      console.error('Error fetching count:', error);
    }
  };

  const fetchAllSigns = async () => {
    try {
      const signsRef = ref(storage, 'gs://elite-ios-a6a68.appspot.com/signs/all-signs.json');
      const url = await getDownloadURL(signsRef);
      const response = await fetch(url);
      const data = await response.json();
      setAllSigns(data);
    } catch (error) {
      console.error('Error fetching signs:', error);
    }
  };
  
  const loadIabCategories = async () => {
    setIabCategoriesLoading(true);
    setIabCategoriesError('');
    try {
      const res = await fetch(`${PERION_API_BRIDGE_BASE}/api/iab-categories`);
      const data = await res.json();
      setIabCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('Failed to load IAB categories:', e?.message || e);
      setIabCategoriesError('Failed to load IAB categories');
      setIabCategories([]);
    } finally {
      setIabCategoriesLoading(false);
    }
  };
  
  const getIabName = (code) => {
    const c = String(code || '').trim();
    if (!c) return '';
    const found = iabCategories.find(x => String(x.code || '').trim() === c);
    return found?.name || '';
  };

  const fetchMissingSignInfo = async () => {
    const missingIds = creatives
      .map(c => c.signId || c.adunit_id)
      .filter(id => id && !allSigns.find(s => s.adunit_id === id || s.id === id) && !signInfo[id]);

    if (missingIds.length === 0) return;

    try {
      const uniqueIds = [...new Set(missingIds)];
      const newSignInfo = { ...signInfo };
      
      // Fetch each sign individually using the new match endpoint
      await Promise.all(uniqueIds.map(async (id) => {
        try {
          // Use the full ID for lookup as requested
          const response = await fetch(`https://vue-analytics-api.fly.dev/api/signs/match?asset_name=${id}`);
          if (response.ok) {
            const result = await response.json();
            if (result.success && (result.data || result.sign)) {
              newSignInfo[id] = result.data || result.sign;
            }
          }
        } catch (err) {
          console.error(`Error fetching sign ${id}:`, err);
        }
      }));

      setSignInfo(newSignInfo);
    } catch (error) {
      console.error('Error in fetchMissingSignInfo:', error);
    }
  };

  const fetchCreatives = async () => {
    try {
      setLoading(true);
      let q = query(collection(db, 'creatives'), orderBy('createdAt', 'desc'), limit(ITEMS_PER_PAGE));
      if (page > 1 && lastDocs[page - 2]) {
        q = query(collection(db, 'creatives'), orderBy('createdAt', 'desc'), startAfter(lastDocs[page - 2]), limit(ITEMS_PER_PAGE));
      }
      const querySnapshot = await getDocs(q);
      const creativesList = querySnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data(), _docSnap: doc }))
        .filter((c) => !HIDDEN_CREATIVE_IDS.has(c.id));
      setCreatives(creativesList);
      
      const newLastDocs = [...lastDocs];
      newLastDocs[page - 1] = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastDocs(newLastDocs);

      // Fetch user info
      const userIds = [...new Set(creativesList.map(c => c.userId))].filter(Boolean);
      const userInfoMap = { ...userInfo };
      for (const userId of userIds) {
        if (!userInfoMap[userId]) {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) userInfoMap[userId] = userDoc.data();
        }
      }
      setUserInfo(userInfoMap);

    } catch (error) {
      console.error('Error fetching creatives:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredCreatives = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'creatives'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const creativesList = querySnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((c) => c.moderationStatus === filterStatus)
        .filter((c) => !HIDDEN_CREATIVE_IDS.has(c.id));
      setCreatives(creativesList);

      // Fetch user info for filtered results
      const userIds = [...new Set(creativesList.map(c => c.userId))].filter(Boolean);
      const userInfoMap = { ...userInfo };
      for (const userId of userIds) {
        if (!userInfoMap[userId]) {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) userInfoMap[userId] = userDoc.data();
        }
      }
      setUserInfo(userInfoMap);
    } catch (error) {
      console.error('Error fetching filtered creatives:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (creative) => {
    try {
      const creativeRef = doc(db, 'creatives', creative.id);
      await updateDoc(creativeRef, { moderationStatus: 'approved', approvedAt: new Date() });
      await logCreativeApproval(creative.id, creative.name || creative.id);
      fetchCreatives();
    } catch (e) {
      alert('Approval failed');
    }
  };

  const openRejectModal = (creative) => {
    setSelectedCreative(creative);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleReject = async (creative) => {
    if (!rejectReason.trim()) return;
    try {
      const creativeRef = doc(db, 'creatives', creative.id);
      await updateDoc(creativeRef, { moderationStatus: 'rejected', rejectionReason: rejectReason, rejectedAt: new Date() });
      await logCreativeRejection(creative.id, creative.name || creative.id, rejectReason);
      setShowRejectModal(false);
      fetchCreatives();
    } catch (e) {
      alert('Rejection failed');
    }
  };

  const openDetailModal = (creative) => {
    setDetailCreative(creative);
    setDetailPreviewFailed(false);
    setSubmissionUrlDraft(
      creative?.croppedImageUrl || creative?.originalImageUrl || creative?.croppedUrl || creative?.originalUrl || ''
    );
    setShowDetailModal(true);
    // prefill IAB selection from the creative doc if present
    const existing = Array.isArray(creative?.iab_categories) ? creative.iab_categories[0] : '';
    if (existing) {
      setSelectedIabByCreativeId(prev => ({ ...prev, [creative.id]: String(existing).trim() }));
    }
  };

  const handleSaveSubmissionUrl = async () => {
    if (!detailCreative?.id) return;
    const nextUrl = String(submissionUrlDraft || '').trim();
    if (!nextUrl) {
      alert('Submission URL cannot be empty.');
      return;
    }
    try {
      new URL(nextUrl.startsWith('http') ? nextUrl : `https://${nextUrl}`);
    } catch (_) {
      alert('Please enter a valid URL.');
      return;
    }

    setSavingSubmissionUrl(true);
    try {
      const creativeRef = doc(db, 'creatives', detailCreative.id);
      // Prefer croppedImageUrl as the submission/preview source (checked first everywhere)
      await updateDoc(creativeRef, {
        croppedImageUrl: nextUrl,
        updatedAt: new Date().toISOString()
      });

      const updated = { ...detailCreative, croppedImageUrl: nextUrl };
      setDetailCreative(updated);
      setCreatives((prev) => prev.map((c) => (c.id === detailCreative.id ? { ...c, croppedImageUrl: nextUrl } : c)));
      setDetailPreviewFailed(false);
      setFailedPreviewCreativeIds((prev) => {
        const next = new Set(prev);
        next.delete(detailCreative.id);
        return next;
      });
    } catch (e) {
      console.error('Failed to save submission URL:', e);
      alert('Failed to save submission URL.');
    } finally {
      setSavingSubmissionUrl(false);
    }
  };

  const openCampaignById = async (campaignId) => {
    if (!campaignId) return;
    setOpeningCampaignId(campaignId);
    try {
      const snap = await getDoc(doc(db, 'campaigns', campaignId));
      if (!snap.exists()) {
        alert('Campaign not found');
        return;
      }
      setSelectedCampaign({ id: snap.id, ...snap.data() });
    } catch (err) {
      console.error('openCampaignById:', err);
      alert('Could not open campaign');
    } finally {
      setOpeningCampaignId(null);
    }
  };

  const renderCreativeCampaignBlock = (creativeId) => {
    if (campaignIndexLoading) {
      return (
        <span style={{ fontSize: 11, color: 'var(--ms-text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FaSpinner className="fa-spin" /> Resolving campaigns…
        </span>
      );
    }
    if (campaignIndexError) {
      return <span style={{ fontSize: 11, color: '#d13438' }}>{campaignIndexError}</span>;
    }
    const links = creativeToCampaigns[creativeId] || [];
    if (links.length === 0) {
      return <span style={{ fontSize: 11, color: 'var(--ms-text-secondary)', fontStyle: 'italic' }}>No linked campaigns</span>;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {links.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openCampaignById(c.id);
            }}
            disabled={!!openingCampaignId}
            className="f-button f-button-secondary"
            style={{
              fontSize: 11,
              padding: '6px 10px',
              height: 'auto',
              minHeight: 0,
              textAlign: 'left',
              justifyContent: 'flex-start',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              opacity: openingCampaignId && openingCampaignId !== c.id ? 0.6 : 1
            }}
          >
            {openingCampaignId === c.id ? (
              <FaSpinner className="fa-spin" style={{ marginRight: 8 }} />
            ) : null}
            {c.name}
          </button>
        ))}
      </div>
    );
  };

  const isVideo = (url) => url && (url.includes('.mp4') || url.includes('video'));
  const formatDate = (ts) => ts ? dayjs(ts.seconds * 1000).format('MMM D, YYYY h:mm A') : '—';

  const isUnsupportedInlineUrl = (url, creative) => {
    if (!url) return true;
    const raw = String(url).trim().split('?')[0];
    let path = raw;
    try {
      path = new URL(raw.startsWith('http') ? raw : 'https://' + raw).pathname || raw;
    } catch (_) { /* keep path */ }
    const ext = path.split('.').pop()?.toLowerCase() || '';
    const unsupported = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx']);
    if (unsupported.has(ext)) return true;
    const inferred = inferMediaFromUrl(url, creative || {});
    return inferred.type !== 'image' && inferred.type !== 'video';
  };

  const getPreviewUrl = (creative) =>
    creative?.croppedImageUrl || creative?.originalImageUrl || creative?.croppedUrl || creative?.originalUrl || '';

  const getPlacementPreviewEntries = (creative) => {
    const map = creative?.placementPreviewRenders;
    if (!map || typeof map !== 'object') return [];
    return Object.entries(map)
      .filter(([, v]) => v?.url)
      .map(([openRtbUuid, v]) => ({
        openRtbUuid,
        url: v.url,
        createdAt: v.createdAt || null
      }));
  };

  const openSignPreviewModal = (creative, e) => {
    if (e) e.stopPropagation();
    const entries = getPlacementPreviewEntries(creative);
    if (entries.length === 0) return;
    setSignPreviewCreative(creative);
    setSignPreviewSelectedIndex(0);
    setShowSignPreviewModal(true);
  };

  const closeSignPreviewModal = () => {
    setShowSignPreviewModal(false);
    setSignPreviewCreative(null);
    setSignPreviewSelectedIndex(0);
  };

  const markPreviewFailed = (creativeId) => {
    setFailedPreviewCreativeIds((prev) => new Set(prev).add(creativeId));
  };

  const PreviewFallbackLink = ({ url, style = {} }) => {
    if (!url) return <span style={{ ...style, color: 'var(--ms-text-secondary)', fontSize: 12 }}>No preview URL</span>;
    const displayText = url.length <= 50 ? url : 'Click here to open';
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          fontSize: 13,
          color: 'var(--ms-primary)',
          textDecoration: 'underline',
          wordBreak: 'break-all',
          textAlign: 'center'
        }}
      >
        {displayText}
      </a>
    );
  };

  const getSignInfo = (signId) => {
    if (!signId) return null;
    return allSigns.find(s => s.adunit_id === signId || s.id === signId) || signInfo[signId];
  };
  
  const shouldShowPerionSubmit = (creative) => {
    if (!creative) return false;
    const status = String(creative.moderationStatus || creative.status || '').toLowerCase();
    const flagged = creative?.nondsp === true || ['pending', 'uploaded', 'flagged'].includes(status);
    if (!flagged) return false;
    // must have at least one seller id or be able to infer from sign
    const sellerIds = getSellerIdsForCreative(creative);
    return sellerIds.length > 0;
  };
  
  const getCreativeResolution = (creative) => {
    const dim = String(creative?.dimensions || '').trim();
    if (dim && dim.includes('x')) return dim;
    const sign = getSignInfo(creative?.signId || creative?.adunit_id);
    const res =
      sign?.Resolution ||
      sign?.resolution ||
      sign?.ad_formats ||
      sign?.preferred_ad_format ||
      sign?.slot_dimensions ||
      '';
    return String(res || '').trim();
  };
  
  const getSellerIdsForCreative = (creative) => {
    const fromCreative = Array.isArray(creative?.sellerIds) ? creative.sellerIds : [];
    const normalizedFromCreative = fromCreative.map(x => String(x || '').trim()).filter(Boolean);
    if (normalizedFromCreative.length > 0) return Array.from(new Set(normalizedFromCreative));
    
    const sign = getSignInfo(creative?.signId || creative?.adunit_id);
    const seller =
      sign?.['Seller ID'] ||
      sign?.seller_id ||
      sign?.sellerId ||
      sign?.publisher_id ||
      sign?.publisherId ||
      '';
    const normalized = String(seller || '').trim();
    return normalized ? [normalized] : [];
  };
  
  const getDspCreativeIdForSeller = (sellerId, firebaseCreativeId) => {
    const pub = String(sellerId || '').trim();
    const cid = String(firebaseCreativeId || '').trim();
    return `VUE-CL-${pub}_${cid}`;
  };

  /** Stored + expected Perion/Vue DSP creative ids for detail modal */
  const getDspCreativeIdDisplayEntries = (creative) => {
    if (!creative) return [];
    const entries = [];
    const seen = new Set();
    const addEntry = (sellerId, dspId, source) => {
      const id = String(dspId || '').trim();
      if (!id) return;
      const key = `${String(sellerId || '').trim()}|${id}`;
      if (seen.has(key)) return;
      seen.add(key);
      entries.push({
        sellerId: String(sellerId || '').trim() || null,
        dspId: id,
        source
      });
    };

    const map =
      creative?.dspCreativeIdMap && typeof creative.dspCreativeIdMap === 'object'
        ? creative.dspCreativeIdMap
        : null;
    if (map) {
      Object.entries(map).forEach(([sellerId, dspId]) => addEntry(sellerId, dspId, 'stored'));
    }

    const single = String(
      creative?.dspCreativeId || creative?.dspCreativeID || creative?.dspId || ''
    ).trim();
    if (single) addEntry(null, single, 'stored');

    if (Array.isArray(creative?.dspCreativeIds)) {
      creative.dspCreativeIds.forEach((dspId) => addEntry(null, dspId, 'stored'));
    }

    const nameAsId = String(creative?.name || '').trim();
    if (nameAsId.startsWith('VUE-CL-')) addEntry(null, nameAsId, 'stored');

    for (const sellerId of getSellerIdsForCreative(creative)) {
      const existing = String(map?.[sellerId] || '').trim();
      if (!existing) {
        addEntry(sellerId, getDspCreativeIdForSeller(sellerId, creative.id), 'expected');
      }
    }

    return entries;
  };
  
  // Infer media type/format from the actual URL first (prevents stale creative.fileType from forcing mp4)
  const inferMediaFromUrl = (url, creative) => {
    const raw = String(url || '').trim();
    const clean = raw.split('?')[0];
    const path = (() => {
      try { return new URL(clean.startsWith('http') ? clean : `https://${clean}`).pathname || clean; }
      catch { return clean; }
    })();
    const ext = path.split('.').pop()?.toLowerCase() || '';
    const videoExts = new Set(['mp4', 'mov', 'webm', 'm4v']);
    const imageExts = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp']);

    if (videoExts.has(ext)) {
      return { type: 'video', format: ext === 'mov' ? 'video/quicktime' : ext === 'webm' ? 'video/webm' : 'video/mp4' };
    }
    if (imageExts.has(ext)) {
      return { type: 'image', format: ext === 'png' ? 'image/png' : ext === 'gif' ? 'image/gif' : ext === 'webp' ? 'image/webp' : 'image/jpeg' };
    }

    // Fallback to filename, then creative.fileType
    const fileName = String(creative?.fileName || '').toLowerCase();
    if (fileName.endsWith('.mp4')) return { type: 'video', format: 'video/mp4' };
    if (fileName.endsWith('.mov')) return { type: 'video', format: 'video/quicktime' };
    if (fileName.endsWith('.webm')) return { type: 'video', format: 'video/webm' };
    if (fileName.endsWith('.png')) return { type: 'image', format: 'image/png' };
    if (fileName.endsWith('.gif')) return { type: 'image', format: 'image/gif' };
    if (fileName.endsWith('.webp')) return { type: 'image', format: 'image/webp' };
    if (String(creative?.fileType || '').toLowerCase() === 'video') return { type: 'video', format: 'video/mp4' };
    return { type: 'image', format: 'image/jpeg' };
  };

  const getFormatFromCreative = (creative) => {
    const url = creative?.croppedImageUrl || creative?.originalImageUrl || creative?.croppedUrl || creative?.originalUrl || '';
    return inferMediaFromUrl(url, creative).format;
  };
  
  const handleApproveAndSubmitToPerion = async (creative) => {
    const iabCode = String(selectedIabByCreativeId?.[creative?.id] || '').trim();
    if (!iabCode) {
      alert('Please select an IAB category first.');
      return;
    }
    
    const sellerIds = getSellerIdsForCreative(creative);
    if (sellerIds.length === 0) {
      alert('No publisher (Seller ID) found for this creative.');
      return;
    }
    
    const resolution = getCreativeResolution(creative);
    const url = creative?.croppedImageUrl || creative?.originalImageUrl || creative?.croppedUrl || creative?.originalUrl || '';
    if (!url) {
      alert('Missing creative media URL (croppedImageUrl/originalImageUrl).');
      return;
    }
    const inferred = inferMediaFromUrl(url, creative);
    
    let width = 0;
    let height = 0;
    if (resolution && resolution.includes('x')) {
      const [w, h] = resolution.split('x').map(v => parseInt(v, 10));
      if (Number.isFinite(w)) width = w;
      if (Number.isFinite(h)) height = h;
    }
    if (!width) width = Number(creative?.width || 1920);
    if (!height) height = Number(creative?.height || 1080);
    
    if (!window.confirm(`Approve and submit this creative to Perion?\n\nIAB: ${iabCode}${getIabName(iabCode) ? ` — ${getIabName(iabCode)}` : ''}\nPublishers: ${sellerIds.join(', ')}`)) {
      return;
    }
    
    setPerionSubmitting(true);
    try {
      const dspCreativeIdMap = (creative?.dspCreativeIdMap && typeof creative.dspCreativeIdMap === 'object') ? { ...creative.dspCreativeIdMap } : {};
      const dspCreativeIds = Array.isArray(creative?.dspCreativeIds) ? [...creative.dspCreativeIds] : [];
      const nextSellerIds = Array.isArray(creative?.sellerIds) ? [...creative.sellerIds] : [];
      
      const statusUpdates = {};
      let firstDspId = '';
      let submittedCount = 0;
      
      for (const sellerId of sellerIds) {
        const existing = String(dspCreativeIdMap?.[sellerId] || '').trim();
        const dspCreativeId = existing || getDspCreativeIdForSeller(sellerId, creative.id);
        if (!firstDspId) firstDspId = dspCreativeId;
        
        const payload = {
          creative_id: dspCreativeId,
          name: dspCreativeId, // requirement: creative name should also be the dsp creative id
          advertiser_domain: creative?.advertiser_domain || creative?.clientDomain || creative?.domain || 'vuebillboards.com',
          publisher_id: sellerId,
          media_files: [
            {
              url,
              type: inferred.type,
              format: inferred.format,
              width,
              height
            }
          ],
          iab_categories: [iabCode]
        };
        
        const res = await fetch(`${PERION_API_BRIDGE_BASE}/api/creatives`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          console.error('Perion submit failed:', { sellerId, dspCreativeId, data });
          alert(`Perion submit failed for ${sellerId}: ${data?.message || 'Unknown error'}`);
          continue;
        }
        
        dspCreativeIdMap[sellerId] = dspCreativeId;
        if (!dspCreativeIds.includes(dspCreativeId)) dspCreativeIds.push(dspCreativeId);
        if (!nextSellerIds.includes(sellerId)) nextSellerIds.push(sellerId);
        statusUpdates[`statuses.${sellerId}`] = 'submitted';
        submittedCount += 1;
      }
      
      if (submittedCount === 0) {
        alert('No publishers were successfully submitted. Check console for details.');
        return;
      }
      
      const creativeRef = doc(db, 'creatives', creative.id);
      await updateDoc(creativeRef, {
        ...statusUpdates,
        moderationStatus: 'submitted',
        approvedByAdmin: true,
        approvedByAdminAt: new Date().toISOString(),
        lastSubmittedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        iab_categories: [iabCode],
        dspCreativeIds,
        dspCreativeIdMap,
        sellerIds: nextSellerIds,
        // store a primary display name as the first dsp id we used
        name: firstDspId || creative?.name || creative.id,
        // keep compatibility with firestore timestamp patterns if used elsewhere
        perionSubmittedAt: Timestamp.now()
      });
      
      alert(`Submitted to Perion for ${submittedCount} publisher${submittedCount > 1 ? 's' : ''}.`);
      
      // refresh list + modal data
      await fetchCreatives();
      const refreshed = await getDoc(creativeRef);
      if (refreshed.exists()) setDetailCreative({ id: refreshed.id, ...refreshed.data() });
    } catch (e) {
      console.error('Approve & submit error:', e);
      alert('Approve & Submit to Perion failed. Check console for details.');
    } finally {
      setPerionSubmitting(false);
    }
  };

  const filteredAndSortedCreatives = creatives; // Simplified for overhaul

  const totalPages = Math.ceil(totalCreatives / ITEMS_PER_PAGE);

  return (
    <>
      <div className="f-page-enter mx-auto max-w-[1920px] px-4 pb-8 md:px-6">
        <div className="f-header rounded-xl border border-slate-200/80 shadow-sm">
          <div>
            <h1 className="f-title">Creative Moderation</h1>
            <p className="f-subtitle mb-0">Review and moderate all uploaded sign creatives</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="f-input" style={{ width: 160 }}>
              <option value="All">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <button onClick={() => setSortAscending(!sortAscending)} className="f-button f-button-secondary">
              {sortAscending ? 'Oldest First' : 'Newest First'}
            </button>
          </div>
        </div>

        <div style={{ padding: '0 32px 32px 32px' }}>
          <div className="f-grid">
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, gridColumn: '1 / -1' }}><FaSpinner className="fa-spin" /> Loading creatives...</div>
            ) : creatives.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, gridColumn: '1 / -1' }}>No creatives found matching filters.</div>
            ) : (
              creatives.map((creative) => (
                <div key={creative.id} className="f-card" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }} onClick={() => openDetailModal(creative)}>
                  <div style={{ position: 'relative', paddingTop: '100%', background: '#f3f2f1' }}>
                    {(() => {
                      const url = getPreviewUrl(creative);
                      const unsupported = isUnsupportedInlineUrl(url, creative);
                      const failed = failedPreviewCreativeIds.has(creative.id);
                      if (unsupported || failed) {
                        return (
                          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PreviewFallbackLink url={url} />
                          </div>
                        );
                      }
                      if (isVideo(creative.originalImageUrl)) {
                        return (
                          <video
                            src={creative.originalImageUrl}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={() => markPreviewFailed(creative.id)}
                          />
                        );
                      }
                      return (
                        <img
                          src={url}
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                          alt=""
                          onError={() => markPreviewFailed(creative.id)}
                        />
                      );
                    })()}
                    <div style={{ position: 'absolute', bottom: 8, right: 8 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 2, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', background: creative.moderationStatus === 'approved' ? '#dcfce7' : creative.moderationStatus === 'rejected' ? '#fee2e2' : '#fffbeb', color: creative.moderationStatus === 'approved' ? '#166534' : creative.moderationStatus === 'rejected' ? '#991b1b' : '#92400e' }}>
                        {creative.moderationStatus || 'Pending'}
                      </span>
                    </div>
                  </div>
                  <div style={{ padding: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{creative.name || creative.id}</div>
                    <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                      <span>{formatDate(creative.createdAt)}</span>
                      {creative.contentScore !== undefined && (
                        <span style={{ fontWeight: 700, color: creative.contentScore > 70 ? '#166534' : creative.contentScore > 40 ? '#92400e' : '#d13438' }}>
                          {creative.contentScore}
                        </span>
                      )}
                    </div>
                    
                    {creative.signId || creative.adunit_id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ fontSize: 11, color: 'var(--ms-primary)', fontWeight: 500 }}>
                          {getSignInfo(creative.signId || creative.adunit_id)?.asset_name || 'Sign: ' + (creative.signId || creative.adunit_id)}
                        </div>
                        {/* Show flagged status on card if exists */}
                        {(creative.moderationStatus === 'flagged' || 
                          (creative.statuses && Object.values(creative.statuses).some(s => s.toLowerCase() === 'flagged')) ||
                          (getSignInfo(creative.signId || creative.adunit_id)?.statuses && 
                           Object.values(getSignInfo(creative.signId || creative.adunit_id).statuses).some(s => s.toLowerCase() === 'flagged'))) && (
                          <div style={{ fontSize: 10, color: '#d13438', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#d13438' }}></span>
                            FLAGGED BY OWNER
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)', fontStyle: 'italic' }}>No sign linked</div>
                    )}

                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', marginBottom: 6, letterSpacing: '0.04em' }}>Campaigns</div>
                      {renderCreativeCampaignBlock(creative.id)}
                    </div>
                    
                    <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {getPlacementPreviewEntries(creative).length > 0 && (
                        <button
                          type="button"
                          className="f-button f-button-secondary"
                          style={{ flex: 1, height: 28, fontSize: 12, minWidth: 120 }}
                          onClick={(e) => openSignPreviewModal(creative, e)}
                        >
                          <FaEye style={{ marginRight: 6 }} />
                          Sign Preview
                        </button>
                      )}
                      {creative.moderationStatus !== 'approved' && <button className="f-button" style={{ flex: 1, height: 28, fontSize: 12 }} onClick={(e) => { e.stopPropagation(); handleApprove(creative); }}>Approve</button>}
                      {creative.moderationStatus !== 'rejected' && <button className="f-button f-button-secondary" style={{ flex: 1, height: 28, fontSize: 12, color: '#d13438' }} onClick={(e) => { e.stopPropagation(); openRejectModal(creative); }}>Reject</button>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {filterStatus === 'All' && totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 32 }}>
              <button className="f-button f-button-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
              <span style={{ alignSelf: 'center', fontSize: 14, color: 'var(--ms-text-secondary)' }}>Page {page} of {totalPages}</span>
              <button className="f-button f-button-secondary" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && detailCreative && (
        <div className="dashboard-modal-overlay" style={{ zIndex: 10000, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowDetailModal(false)}>
          <div className="f-card" style={{ width: '100%', maxWidth: 1000, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '24px 32px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Creative Details</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: 12, color: 'var(--ms-text-secondary)' }}>ID: {detailCreative.id}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--ms-text-secondary)', cursor: 'pointer' }}>&times;</button>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', padding: 32 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 40 }}>
                {/* Left Column: Preview */}
                <div>
                  <div style={{ background: '#f3f2f1', borderRadius: 8, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400, boxShadow: 'inset 0 0 10px rgba(0,0,0,0.05)' }}>
                    {(() => {
                      const url = getPreviewUrl(detailCreative);
                      const unsupported = isUnsupportedInlineUrl(url, detailCreative);
                      if (unsupported || detailPreviewFailed) {
                        return <PreviewFallbackLink url={url} style={{ minHeight: 200 }} />;
                      }
                      if (isVideo(detailCreative.originalImageUrl)) {
                        return (
                          <video
                            src={detailCreative.originalImageUrl}
                            controls
                            style={{ maxWidth: '100%', maxHeight: 600, borderRadius: 4, boxShadow: 'var(--ms-shadow)' }}
                            onError={() => setDetailPreviewFailed(true)}
                          />
                        );
                      }
                      return (
                        <img
                          src={url}
                          style={{ maxWidth: '100%', maxHeight: 600, borderRadius: 4, boxShadow: 'var(--ms-shadow)' }}
                          alt=""
                          onError={() => setDetailPreviewFailed(true)}
                        />
                      );
                    })()}
                  </div>

                  {getPlacementPreviewEntries(detailCreative).length > 0 && (
                    <div style={{ marginTop: 24 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <h4 style={{ margin: 0, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', letterSpacing: '0.05em' }}>
                          Sign placement preview
                        </h4>
                        <button
                          type="button"
                          className="f-button f-button-secondary"
                          style={{ height: 28, fontSize: 12 }}
                          onClick={() => openSignPreviewModal(detailCreative)}
                        >
                          <FaEye style={{ marginRight: 6 }} />
                          Open preview
                        </button>
                      </div>
                      <div style={{ background: '#000', borderRadius: 8, overflow: 'hidden' }}>
                        <video
                          src={getPlacementPreviewEntries(detailCreative)[0].url}
                          controls
                          playsInline
                          style={{ width: '100%', maxHeight: 320, display: 'block' }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {detailCreative.moderationStatus === 'rejected' && detailCreative.rejectionReason && (
                    <div style={{ marginTop: 24, padding: 16, background: '#fee2e2', borderRadius: 4, borderLeft: '4px solid #ef4444' }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#991b1b', fontWeight: 600 }}>Rejection Reason</h4>
                      <p style={{ margin: 0, fontSize: 14, color: '#991b1b' }}>{detailCreative.rejectionReason}</p>
                    </div>
                  )}
                </div>

                {/* Right Column: Information */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                  {/* Status Section */}
                  <section>
                    <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', letterSpacing: '0.05em' }}>Status & Timeline</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: detailCreative.moderationStatus === 'approved' ? '#dcfce7' : detailCreative.moderationStatus === 'rejected' ? '#fee2e2' : detailCreative.moderationStatus === 'flagged' ? '#fffbeb' : '#f3f2f1', color: detailCreative.moderationStatus === 'approved' ? '#166534' : detailCreative.moderationStatus === 'rejected' ? '#991b1b' : detailCreative.moderationStatus === 'flagged' ? '#92400e' : '#323130' }}>
                          {detailCreative.moderationStatus?.toUpperCase() || 'PENDING'}
                        </span>
                        {detailCreative.contentScore !== undefined && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>Score:</span>
                            <span style={{ fontSize: 14, fontWeight: 700, color: detailCreative.contentScore > 70 ? '#166534' : detailCreative.contentScore > 40 ? '#92400e' : '#d13438' }}>
                              {detailCreative.contentScore}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* AI Content Flags */}
                      {detailCreative.contentFlags && detailCreative.contentFlags.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                          {detailCreative.contentFlags.map((flag, idx) => (
                            <span key={idx} style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 4, fontWeight: 600, border: '1px solid #fde68a' }}>
                              {flag.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Creative-level Owner Statuses (if any) */}
                      {detailCreative.statuses && Object.keys(detailCreative.statuses).length > 0 && (
                        <div style={{ marginTop: 8, padding: '12px', background: '#fff', borderRadius: 8, border: '1px solid #eee', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#666', marginBottom: 8, textTransform: 'uppercase', borderBottom: '1px solid #f0f0f0', paddingBottom: 4 }}>Media Owner Reviews</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {Object.entries(detailCreative.statuses).map(([owner, status]) => (
                              <div key={owner} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{owner}</span>
                                <span style={{ 
                                  fontSize: 10, 
                                  fontWeight: 700, 
                                  padding: '2px 8px',
                                  borderRadius: 4,
                                  background: status.toLowerCase() === 'flagged' ? '#fee2e2' : '#dcfce7',
                                  color: status.toLowerCase() === 'flagged' ? '#991b1b' : '#166534' 
                                }}>
                                  {status.toUpperCase()}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)', marginTop: 8 }}>
                        <div><b>Created:</b> {formatDate(detailCreative.createdAt)}</div>
                        {detailCreative.approvedAt && <div><b>Approved:</b> {formatDate(detailCreative.approvedAt)}</div>}
                        {detailCreative.rejectedAt && <div><b>Rejected:</b> {formatDate(detailCreative.rejectedAt)}</div>}
                        {detailCreative.dimensions && <div><b>Dimensions:</b> {detailCreative.dimensions}</div>}
                      </div>
                    </div>
                  </section>

                  <section>
                    <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', letterSpacing: '0.05em' }}>Advertiser Domain</h3>
                    <div style={{ padding: 16, background: '#f8f9fa', borderRadius: 8, fontSize: 14 }}>
                      {(() => {
                        const domain = detailCreative.advertiser_domain || detailCreative.clientDomain || detailCreative.domain;
                        if (domain) {
                          return <span style={{ fontWeight: 600, color: '#1e293b' }}>{domain}</span>;
                        }
                        return <span style={{ color: '#ef4444', fontStyle: 'italic' }}>Not set</span>;
                      })()}
                    </div>
                  </section>

                  <section>
                    <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', letterSpacing: '0.05em' }}>Submission URL</h3>
                    <p style={{ margin: '0 0 10px 0', fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                      Media URL used for preview and when submitting this creative to Perion.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                          <FaLink style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ms-text-secondary)', fontSize: 12, pointerEvents: 'none' }} />
                          <input
                            type="url"
                            className="f-input"
                            value={submissionUrlDraft}
                            onChange={(e) => setSubmissionUrlDraft(e.target.value)}
                            placeholder="https://…"
                            style={{ width: '100%', paddingLeft: 32, fontSize: 13 }}
                          />
                        </div>
                        <button
                          type="button"
                          className="f-button"
                          style={{ height: 'auto', minHeight: 36, padding: '0 16px', whiteSpace: 'nowrap' }}
                          disabled={
                            savingSubmissionUrl ||
                            String(submissionUrlDraft || '').trim() === String(getPreviewUrl(detailCreative) || '').trim()
                          }
                          onClick={handleSaveSubmissionUrl}
                        >
                          {savingSubmissionUrl ? <FaSpinner className="fa-spin" /> : 'Save'}
                        </button>
                      </div>
                      {getPreviewUrl(detailCreative) ? (
                        <a
                          href={getPreviewUrl(detailCreative)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 12, color: 'var(--ms-primary)', wordBreak: 'break-all' }}
                        >
                          Open current URL
                        </a>
                      ) : null}
                    </div>
                  </section>

                  <section>
                    <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', letterSpacing: '0.05em' }}>Perion / Vue DSP Creative ID</h3>
                    {(() => {
                      const dspEntries = getDspCreativeIdDisplayEntries(detailCreative);
                      if (dspEntries.length === 0) {
                        return (
                          <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, fontSize: 13, color: 'var(--ms-text-secondary)', fontStyle: 'italic' }}>
                            No DSP creative ID yet. Link a sign with a publisher (Seller ID) to see the expected ID, or submit to Perion.
                          </div>
                        );
                      }
                      return (
                        <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {dspEntries.map((entry, idx) => (
                            <div key={`${entry.sellerId || 'global'}-${entry.dspId}-${idx}`}>
                              {entry.sellerId ? (
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 4 }}>
                                  Publisher {entry.sellerId}
                                  {entry.source === 'expected' ? (
                                    <span style={{ marginLeft: 8, fontWeight: 500, fontStyle: 'italic' }}>(expected on submit)</span>
                                  ) : null}
                                </div>
                              ) : entry.source === 'expected' ? (
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 4, fontStyle: 'italic' }}>
                                  Expected on submit
                                </div>
                              ) : null}
                              <code
                                style={{
                                  display: 'block',
                                  fontSize: 12,
                                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                  wordBreak: 'break-all',
                                  padding: '8px 10px',
                                  background: '#fff',
                                  borderRadius: 4,
                                  border: '1px solid var(--ms-border-light, #e2e8f0)'
                                }}
                              >
                                {entry.dspId}
                              </code>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </section>

                  <section>
                    <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', letterSpacing: '0.05em' }}>Campaigns</h3>
                    {renderCreativeCampaignBlock(detailCreative.id)}
                  </section>

                  {/* User Section */}
                  <section>
                    <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', letterSpacing: '0.05em' }}>User Information</h3>
                    <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FaUser style={{ color: 'var(--ms-primary)', fontSize: 14 }} />
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{userInfo[detailCreative.userId]?.name || 'Unknown User'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FaEnvelope style={{ color: 'var(--ms-text-secondary)', fontSize: 14 }} />
                        <span style={{ fontSize: 14 }}>{userInfo[detailCreative.userId]?.email || 'No email provided'}</span>
                      </div>
                      {userInfo[detailCreative.userId]?.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FaPhone style={{ color: 'var(--ms-text-secondary)', fontSize: 14 }} />
                          <span style={{ fontSize: 14 }}>{userInfo[detailCreative.userId]?.phone}</span>
                        </div>
                      )}
                    </div>
                  </section>

                  {/* Sign Section */}
                  <section>
                    <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', letterSpacing: '0.05em' }}>Sign Information</h3>
                    {getSignInfo(detailCreative.signId || detailCreative.adunit_id) ? (
                      <div style={{ background: '#f0f7ff', padding: 16, borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 14 }}><b>Asset:</b> {getSignInfo(detailCreative.signId || detailCreative.adunit_id).asset_name}</div>
                          <div style={{ fontSize: 14 }}><b>Publisher:</b> {getSignInfo(detailCreative.signId || detailCreative.adunit_id).publisher || '—'}</div>
                          <div style={{ fontSize: 14 }}><b>Location:</b> {getSignInfo(detailCreative.signId || detailCreative.adunit_id).city}, {getSignInfo(detailCreative.signId || detailCreative.adunit_id).state}</div>
                          <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', marginTop: 4 }}><b>Sign ID:</b> {detailCreative.signId || detailCreative.adunit_id}</div>
                        </div>

                        {/* Sign Master Statuses Map (from Sign Data) */}
                        {getSignInfo(detailCreative.signId || detailCreative.adunit_id).statuses && 
                         Object.keys(getSignInfo(detailCreative.signId || detailCreative.adunit_id).statuses).length > 0 && (
                          <div style={{ marginTop: 8, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', marginBottom: 8, letterSpacing: '0.02em' }}>Sign-level Statuses</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {Object.entries(getSignInfo(detailCreative.signId || detailCreative.adunit_id).statuses).map(([owner, status]) => (
                                <div key={owner} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.5)', padding: '6px 10px', borderRadius: 4 }}>
                                  <span style={{ fontSize: 13, fontWeight: 600 }}>{owner}</span>
                                  <span style={{ 
                                    fontSize: 11, 
                                    fontWeight: 700, 
                                    padding: '2px 8px', 
                                    borderRadius: 10,
                                    background: status.toLowerCase() === 'flagged' ? '#fee2e2' : '#f0f9ff',
                                    color: status.toLowerCase() === 'flagged' ? '#991b1b' : '#075985',
                                    textTransform: 'uppercase'
                                  }}>
                                    {status}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8, border: '1px dashed #cbd5e1' }}>
                        <div style={{ color: 'var(--ms-text-secondary)', fontStyle: 'italic', fontSize: 13 }}>
                          No sign information linked to this creative.
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                          <b>ID:</b> {detailCreative.signId || detailCreative.adunit_id || 'None'}
                        </div>
                        <button 
                          onClick={() => fetchMissingSignInfo()} 
                          style={{ marginTop: 12, fontSize: 11, color: 'var(--ms-primary)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Retry API lookup
                        </button>
                      </div>
                    )}
                  </section>

                  {/* Actions */}
                  <div style={{ marginTop: 'auto', paddingTop: 24, borderTop: '1px solid var(--ms-border-light)' }}>
                    {/* Perion Submission (DSP / non-DSP flagged creatives) */}
                    {shouldShowPerionSubmit(detailCreative) && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', letterSpacing: '0.05em', marginBottom: 8 }}>
                          Perion Submission
                        </div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                          IAB Category (required)
                        </label>
                        {iabCategoriesLoading ? (
                          <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>Loading categories…</div>
                        ) : iabCategoriesError ? (
                          <div style={{ fontSize: 12, color: '#d13438' }}>{iabCategoriesError}</div>
                        ) : (
                          <select
                            className="f-input"
                            style={{ width: '100%', marginBottom: 10 }}
                            value={selectedIabByCreativeId?.[detailCreative.id] || (Array.isArray(detailCreative?.iab_categories) ? (detailCreative.iab_categories[0] || '') : '')}
                            onChange={(e) => setSelectedIabByCreativeId(prev => ({ ...prev, [detailCreative.id]: e.target.value }))}
                          >
                            <option value="">Select a category…</option>
                            {iabCategories.map((c) => (
                              <option key={c.code} value={c.code}>
                                {c.code} — {c.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', gap: 12 }}>
                      {shouldShowPerionSubmit(detailCreative) && (
                        <button
                          className="f-button"
                          style={{ flex: 1, height: 44, background: '#107c10', borderColor: '#107c10' }}
                          disabled={perionSubmitting || !String(selectedIabByCreativeId?.[detailCreative.id] || '').trim()}
                          onClick={() => handleApproveAndSubmitToPerion(detailCreative)}
                          title={!String(selectedIabByCreativeId?.[detailCreative.id] || '').trim() ? 'Select an IAB category first' : ''}
                        >
                          {perionSubmitting ? <FaSpinner className="fa-spin" /> : <FaCheck style={{ marginRight: 8 }} />}
                          Approve & Submit to Perion
                        </button>
                      )}
                    {detailCreative.moderationStatus !== 'approved' && (
                      <button className="f-button" style={{ flex: 1, height: 44 }} onClick={() => handleApprove(detailCreative)}>
                        Approve Creative
                      </button>
                    )}
                    {detailCreative.moderationStatus !== 'rejected' && (
                      <button className="f-button f-button-secondary" style={{ flex: 1, height: 44, color: '#d13438' }} onClick={() => openRejectModal(detailCreative)}>
                        Reject Creative
                      </button>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedCampaign && (
        <CampaignDetail campaign={selectedCampaign} onClose={() => setSelectedCampaign(null)} />
      )}

      {/* Sign placement preview modal */}
      {showSignPreviewModal && signPreviewCreative && (() => {
        const entries = getPlacementPreviewEntries(signPreviewCreative);
        const selected = entries[signPreviewSelectedIndex] || entries[0];
        if (!selected) return null;
        return (
          <div
            className="dashboard-modal-overlay"
            style={{ zIndex: 10050, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={closeSignPreviewModal}
          >
            <div className="f-card" style={{ width: '100%', maxWidth: 720, padding: 0, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Sign placement preview</h3>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                    {signPreviewCreative.name || signPreviewCreative.id}
                  </p>
                </div>
                <button onClick={closeSignPreviewModal} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--ms-text-secondary)', cursor: 'pointer' }}>&times;</button>
              </div>
              <div style={{ padding: 24 }}>
                {entries.length > 1 && (
                  <label style={{ display: 'block', marginBottom: 12 }}>
                    <span style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--ms-text-secondary)', marginBottom: 6 }}>Sign</span>
                    <select
                      className="f-input"
                      style={{ width: '100%' }}
                      value={signPreviewSelectedIndex}
                      onChange={(e) => setSignPreviewSelectedIndex(Number(e.target.value))}
                    >
                      {entries.map((entry, idx) => (
                        <option key={entry.openRtbUuid} value={idx}>
                          {entry.openRtbUuid}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <div style={{ background: '#000', borderRadius: 8, overflow: 'hidden' }}>
                  <video
                    key={selected.url}
                    src={selected.url}
                    controls
                    playsInline
                    autoPlay
                    style={{ width: '100%', maxHeight: '60vh', display: 'block' }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="dashboard-modal-overlay" style={{ zIndex: 10060, background: 'rgba(0,0,0,0.4)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="f-card" style={{ width: '100%', maxWidth: 400, padding: 32 }}>
            <h3 className="f-title" style={{ fontSize: 20 }}>Reject Creative</h3>
            <p style={{ fontSize: 14, color: 'var(--ms-text-secondary)', marginBottom: 20 }}>Provide a reason for rejection:</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="f-input"
              style={{ width: '100%', height: 100, padding: 12, resize: 'none', marginBottom: 20 }}
              placeholder="Reason..."
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="f-button" style={{ flex: 1, background: '#d13438', borderColor: '#d13438' }} onClick={() => handleReject(selectedCreative)}>Reject</button>
              <button className="f-button f-button-secondary" style={{ flex: 1 }} onClick={() => setShowRejectModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreativesList;
